import {
  AppBase,
  baseFromDoc,
  FirestoreBaseDoc,
  makeDocConverter,
} from '../base/firestore-model-base'
import type { Timestamp } from 'firebase/firestore'
import { dateToJson } from '../../utils/date-util'

/**
 * AddTownCompanyRequest（事業所追加依頼／郵送依頼）
 *
 * - `AddTownCompanyRequestDoc`: Firestore に保存される形（snake_case / Timestamp）
 * - `AddTownCompanyRequest`: アプリで扱う形（camelCase / Date）
 * - 保存先: school_accounts/{schoolAccountId}/projects/{projectId}/requests/{requestId}
 */

export type AddTownCompanyRequestStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled'

export type AddTownCompanyRequestDoc = FirestoreBaseDoc & {
  project_id?: string | null
  project_path?: string | null
  requested_count: number
  status: AddTownCompanyRequestStatus
  is_billed: boolean
  /** 郵送回（データを確定して郵送する時に付与） */
  send_request_number?: string | null
  completed_at?: Timestamp | null
  completed_by_user_type?: 'admin' | 'school' | null
  completed_by_user_id?: string | null
  completed_by_name?: string | null
  completed_by_user_document_path?: string | null
  town_company_references?: string[] | null
}

export type AddTownCompanyRequest = AppBase & {
  project_id?: string
  project_path?: string | null
  requestedCount: number
  status: AddTownCompanyRequestStatus
  isBilled: boolean
  /** 郵送回（データを確定して郵送する時に付与） */
  sendRequestNumber?: string
  completedAt?: Date
  completedByUserType?: 'admin' | 'school'
  completedByUserId?: string
  completedByName?: string
  completedByUserDocumentPath?: string
  townCompanyReferences?: string[] | null
}

export const addTownCompanyRequestDocConverter = makeDocConverter<AddTownCompanyRequestDoc>()

function parseStatus(value: unknown): AddTownCompanyRequestStatus {
  if (value === '対応中') return 'in_progress'
  if (value === '完了') return 'completed'
  if (value === 'キャンセル') return 'cancelled'
  if (value === 'cancel') return 'cancelled'
  if (value === 'pending' || value === 'in_progress' || value === 'completed' || value === 'cancelled') {
    return value as AddTownCompanyRequestStatus
  }
  return 'pending'
}

function refToPath(v: unknown): string | null {
  return typeof v === 'string' ? v : (v as { path?: string; id?: string } | null)?.path ?? (v as { id?: string } | null)?.id ?? null
}

export function toAddTownCompanyRequest(id: string, doc: AddTownCompanyRequestDoc, path: string): AddTownCompanyRequest {
  const base = baseFromDoc(id, doc, path)
  const project_path = doc.project_path
  const project_id = project_path?.split('/').pop() ?? doc.project_id ?? undefined

  return {
    ...base,
    project_id,
    project_path: project_path ?? undefined,
    requestedCount: doc.requested_count,
    status: parseStatus(doc.status),
    isBilled: doc.is_billed,
    sendRequestNumber: doc.send_request_number ?? undefined,
    completedAt: doc.completed_at ? doc.completed_at.toDate() : undefined,
    completedByUserType: doc.completed_by_user_type ?? undefined,
    completedByUserId: doc.completed_by_user_id ?? undefined,
    completedByName: doc.completed_by_name ?? undefined,
    completedByUserDocumentPath: doc.completed_by_user_document_path ?? undefined,
    townCompanyReferences: (() => {
      const raw = (doc as { town_company_references?: unknown }).town_company_references
      if (!Array.isArray(raw)) return undefined
      return raw.map((r) => (typeof r === 'string' ? r : refToPath(r) ?? '')).filter(Boolean)
    })(),
  }
}

/** 履歴等へ保存するためのスナップショット（AddTownCompanyRequest → Firestore 用） */
export function toAddTownCompanyRequestSnapshotJson(request: AddTownCompanyRequest): Record<string, unknown> {
  return {
    project_id: request.project_id ?? null,
    project_path: request.project_path ?? null,
    requested_count: request.requestedCount,
    status: request.status,
    is_billed: request.isBilled,
    send_request_number: request.sendRequestNumber ?? null,
    completed_at: request.completedAt ? dateToJson(request.completedAt) : null,
    completed_by_user_type: request.completedByUserType ?? null,
    completed_by_user_id: request.completedByUserId ?? null,
    completed_by_name: request.completedByName ?? null,
    completed_by_user_document_path: request.completedByUserDocumentPath ?? null,
    town_company_references: request.townCompanyReferences ?? [],
    created_at: dateToJson(request.created_at),
    updated_at: dateToJson(request.updated_at),
    is_deleted: request.is_deleted,
    document_id: request.id,
  }
}
